import { Offcanvas } from 'react-bootstrap';
import { Formik } from 'formik';
import { DateTime as DT } from 'luxon';
import { useTranslation } from 'react-i18next';
import { safeFloatParse } from 'utils/helpers/safeParsing';
import vconst from 'utils/helpers/structs/vconst';
import { useObjectCard } from '../context/ObjectCardProvider';
import ObjectCloseForm from './ObjectCloseForm';
import {
    useObjectStatusHistory,
    type ObjectCloseFormValues,
    type ObjectCloseMode,
    type ObjectStatusChange,
} from './model';

interface OffObjectCloseProps {
    show: boolean;
    mode: ObjectCloseMode;
    onHide: () => void;
    onSuccess: (statusData: ObjectStatusChange) => Promise<boolean>;
}

const OffObjectClose = ({ show, mode, onHide, onSuccess }: OffObjectCloseProps) => {
    const { t } = useTranslation(['Objects']);
    const { vObject, onlySale, onlyService } = useObjectCard();
    const objectId = Number(vObject?.object?.ID || 0) || 0;
    const objectPower = vObject?.object?.objectPower;
    const { history, loading } = useObjectStatusHistory(objectId, show);

    const title = mode === 'return'
        ? (onlyService
            ? t('objectClose.titleReturnService')
            : onlySale
                ? t('objectClose.titleReturnSale')
                : t('objectClose.titleReturn'))
        : mode === 'cancel'
            ? (onlyService
                ? t('toolServiceSaleCancel')
                : onlySale
                    ? t('toolSaleCancel')
                    : t('toolObjectCancel'))
            : (onlyService
                ? t('toolServiceSaleFinished')
                : onlySale
                    ? t('toolSaleFinished')
                    : t('toolObjectFinished'));

    const initialValues: ObjectCloseFormValues = {
        finishedDate: DT.now().setZone('local').toISODate() || '',
        finishConfirm: false,
        comment: '',
    };

    return (
        <Offcanvas show={show} onHide={onHide} placement="end" className="off-object-close">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{title}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                <Formik
                    key={show ? mode : 'closed'}
                    enableReinitialize
                    initialValues={initialValues}
                    onSubmit={async (values, helpers) => {
                        if (mode === 'close') {
                            if (!values.finishConfirm) {
                                helpers.setFieldError('finishConfirm', t('objectClose.errConfirm'));
                                return;
                            }
                            const finished = DT.fromISO(values.finishedDate, { zone: 'local' }).toMillis();
                            if (!finished) {
                                helpers.setFieldError('finishedDate', t('objectClose.errDate'));
                                return;
                            }
                            const ok = await onSuccess({
                                power: safeFloatParse(objectPower || 0) || 0,
                                status: vconst.objectFinished,
                                finished,
                            });
                            if (ok) onHide();
                            return;
                        }

                        const comment = String(values.comment || '').trim();
                        if (!comment) {
                            helpers.setFieldError('comment', t('objectClose.errComment'));
                            return;
                        }
                        const ok = await onSuccess({
                            status: mode === 'cancel' ? vconst.objectReject : vconst.objectInWork,
                            comment,
                        });
                        if (ok) onHide();
                    }}
                >
                    {({
                        handleSubmit,
                        isSubmitting,
                        values,
                        setFieldValue,
                        errors,
                    }) => (
                        <ObjectCloseForm
                            mode={mode}
                            values={values}
                            setFieldValue={setFieldValue}
                            handleSubmit={handleSubmit}
                            isSubmitting={isSubmitting}
                            finishedDateError={errors.finishedDate ? String(errors.finishedDate) : ''}
                            commentError={errors.comment ? String(errors.comment) : ''}
                            history={history}
                            historyLoading={loading}
                            autofocus={show}
                            onlySale={onlySale}
                            onlyService={onlyService}
                        />
                    )}
                </Formik>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffObjectClose;
